import React, { useState, useMemo } from 'react';
import { PieChart, Landmark, Heart, BadgeCheck, AlertTriangle } from 'lucide-react';
import { Card, Input } from '../ui';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { cn } from '../../lib/utils';

const COLORS = {
  program: '#10b981', // emerald
  admin: '#64748b', // slate
  fundraising: '#f59e0b' // amber
};

export function Nonprofit990RatioChecker() {
  const [programExpenses, setProgramExpenses] = useState<number>(850000);
  const [adminExpenses, setAdminExpenses] = useState<number>(120000);
  const [fundraisingExpenses, setFundraisingExpenses] = useState<number>(80000);

  const model = useMemo(() => {
    const totalExpenses = programExpenses + adminExpenses + fundraisingExpenses;
    
    const programRatio = totalExpenses > 0 ? (programExpenses / totalExpenses) * 100 : 0;
    const adminRatio = totalExpenses > 0 ? (adminExpenses / totalExpenses) * 100 : 0;
    const fundraisingRatio = totalExpenses > 0 ? (fundraisingExpenses / totalExpenses) * 100 : 0;

    // Standard Charity Navigator / BBB Wise Giving Alliance benchmarks
    // Generally, >= 65% is considered acceptable, >= 75% is good, >= 85% is excellent
    let grade = 'Needs Improvement';
    let gradeColor = 'text-red-500';
    let isHealthy = false;

    if (programRatio >= 85) {
      grade = 'Excellent (4-Star)';
      gradeColor = 'text-emerald-500';
      isHealthy = true;
    } else if (programRatio >= 75) {
      grade = 'Good (3-Star)';
      gradeColor = 'text-blue-500';
      isHealthy = true;
    } else if (programRatio >= 65) {
      grade = 'Acceptable (2-Star)';
      gradeColor = 'text-amber-500';
      isHealthy = true;
    }

    const chartData = [
      { name: 'Program Services', value: programExpenses, color: COLORS.program },
      { name: 'Management & General', value: adminExpenses, color: COLORS.admin },
      { name: 'Fundraising', value: fundraisingExpenses, color: COLORS.fundraising }
    ].filter(d => d.value > 0);

    return {
      totalExpenses,
      programRatio,
      adminRatio,
      fundraisingRatio,
      grade,
      gradeColor,
      isHealthy,
      chartData
    };
  }, [programExpenses, adminExpenses, fundraisingExpenses]);

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  return (
    <div className="max-w-5xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-emerald-800 to-teal-900 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Landmark className="w-6 h-6 mr-3 text-emerald-300" />
            Form 990 Efficiency Ratio Checker
          </h2>
          <p className="text-emerald-100 max-w-xl">
            Evaluate your nonprofit's financial health before you file your IRS Form 990. Calculate your Program Expense Ratio to see how watchdog agencies like Charity Navigator will grade you.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        
        {/* Controls */}
        <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-emerald-600 dark:text-emerald-500 uppercase flex items-center mb-2">
                <Heart className="w-4 h-4 mr-2" /> Program Service Expenses ($)
              </label>
              <Input 
                type="number" 
                value={programExpenses} 
                onChange={(e) => setProgramExpenses(Number(e.target.value))}
                className="text-lg font-bold border-emerald-200 focus-visible:ring-emerald-500"
              />
              <p className="text-xs text-slate-400 mt-1">Money spent directly on your charity's mission.</p>
            </div>
            
            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">
                Management & General ($)
              </label>
              <Input 
                type="number" 
                value={adminExpenses} 
                onChange={(e) => setAdminExpenses(Number(e.target.value))}
              />
              <p className="text-xs text-slate-400 mt-1">Admin, accounting, HR, and board operations.</p>
            </div>
            
            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
              <label className="text-sm font-semibold text-amber-600 dark:text-amber-500 uppercase block mb-2">
                Fundraising Expenses ($)
              </label>
              <Input 
                type="number" 
                value={fundraisingExpenses} 
                onChange={(e) => setFundraisingExpenses(Number(e.target.value))}
              />
              <p className="text-xs text-slate-400 mt-1">Costs to run galas, mailers, and development staff.</p>
            </div>
          </div>
          
          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg flex justify-between items-center border border-slate-100 dark:border-slate-700">
            <span className="font-bold text-slate-500 uppercase text-sm">Total Expenses</span>
            <span className="font-black text-xl">{formatCurrency(model.totalExpenses)}</span>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          <Card className={cn("p-6 border-slate-200 dark:border-slate-800 shadow-xl", model.isHealthy ? "bg-white dark:bg-slate-900" : "bg-red-50 dark:bg-red-900/10")}>
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">Program Expense Ratio</h3>
              {model.isHealthy ? <BadgeCheck className="w-8 h-8 text-emerald-500" /> : <AlertTriangle className="w-8 h-8 text-red-500" />}
            </div>
            
            <div className="flex items-baseline gap-3 mb-2">
              <div className="text-6xl font-black tracking-tight text-slate-900 dark:text-white">
                {model.programRatio.toFixed(1)}%
              </div>
            </div>
            
            <div className={cn("inline-flex px-3 py-1 rounded-full text-sm font-bold bg-opacity-20", model.gradeColor, model.gradeColor.replace('text-', 'bg-'))}>
              Watchdog Grade: {model.grade}
            </div>
            
            <p className="text-sm text-slate-500 mt-4">
              Donors and watchdog agencies typically look for a ratio of <strong>at least 65%</strong> to ensure funds are being spent efficiently on the mission, rather than bloated overhead.
            </p>
          </Card>

          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center">
              <PieChart className="w-4 h-4 mr-2" /> Expense Distribution
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4 items-center">
              <div className="h-[180px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={model.chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={2}
                    >
                      {model.chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: any) => formatCurrency(Number(value))}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center text-sm font-bold text-slate-700 dark:text-slate-300">
                    <div className="w-3 h-3 rounded-full bg-emerald-500 mr-2" /> Program
                  </div>
                  <div className="text-sm font-semibold">{model.programRatio.toFixed(1)}%</div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center text-sm font-bold text-slate-700 dark:text-slate-300">
                    <div className="w-3 h-3 rounded-full bg-slate-500 mr-2" /> Admin
                  </div>
                  <div className="text-sm font-semibold">{model.adminRatio.toFixed(1)}%</div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center text-sm font-bold text-slate-700 dark:text-slate-300">
                    <div className="w-3 h-3 rounded-full bg-amber-500 mr-2" /> Fundraising
                  </div>
                  <div className="text-sm font-semibold">{model.fundraisingRatio.toFixed(1)}%</div>
                </div>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
